import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Header from "./components/layout/Header";
import { SidebarProvider } from "./components/ui/sidebar";
import AppSidebar from "./components/layout/AppSidebar";
import DashboardPage from "./components/dashboard/DashboardPage";
import DepartmentsPage from "./components/departments/DepartmentsPage";
import AnalyticsPage from "./components/analytics/AnalyticsPage";
import TeamPage from "./components/team/TeamPage";
import ResourceLibrary from "./components/filesystem/ResourceLibrary";
import NotFound from "./pages/NotFound";
import SignInPage from "./pages/auth/SignIn";
import { AuthProvider, useAuth } from "./contexts/auth/AuthContext";
import SettingsPage from "./components/settings/SettingsPage";
import LegalDepartmentPage from "./components/legal/LegalDepartmentPage";
import DreamPage from "./components/dream/DreamPage";

const queryClient = new QueryClient();

// Route guard component to handle authentication
const ProtectedRoute = ({ children, requiredRoles = [] }: { children: React.ReactNode, requiredRoles?: string[] }) => {
  const { user, isLoading, currentOrganization, userHasAccess } = useAuth();
  const location = useLocation();

  if (isLoading) {
    // Show loading state while checking authentication
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login if not authenticated
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // If specific roles are required and user doesn't have access
  if (requiredRoles.length > 0 && !userHasAccess(requiredRoles)) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center p-6">
          <h2 className="text-2xl font-bold mb-2">Access Restricted</h2>
          <p className="text-muted-foreground">
            You don't have the required permissions to access this page.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Route guard for auth pages (redirect to dashboard if already logged in)
const AuthRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

// Layout component for authenticated pages
const AppLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <SidebarProvider>
      <div className="flex h-screen w-full overflow-hidden">
        <AppSidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Auth routes */}
            <Route path="/signin" element={
              <AuthRoute>
                <SignInPage />
              </AuthRoute>
            } />
            {/* Redirect signup to signin */}
            <Route path="/signup" element={<Navigate to="/signin" replace />} />
            
            {/* Protected routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <AppLayout>
                  <DashboardPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/departments" element={
              <ProtectedRoute>
                <AppLayout>
                  <DepartmentsPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/departments/:department" element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="p-6">
                    <h1 className="text-3xl font-bold">Department Details</h1>
                  </div>
                </AppLayout>
              </ProtectedRoute>
            } />
            {/* Legal Department Route */}
            <Route path="/departments/legal" element={
              <ProtectedRoute>
                <AppLayout>
                  <LegalDepartmentPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute requiredRoles={['primary_founder', 'co_founder', 'department_head']}>
                <AppLayout>
                  <AnalyticsPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/team" element={
              <ProtectedRoute>
                <AppLayout>
                  <TeamPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/resources" element={
              <ProtectedRoute>
                <AppLayout>
                  <ResourceLibrary />
                </AppLayout>
              </ProtectedRoute>
            } />
            {/* Dream Section Route */}
            <Route path="/dream" element={
              <ProtectedRoute>
                <AppLayout>
                  <DreamPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <AppLayout>
                  <SettingsPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            {/* Default 404 route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
